import { AskQuantityFlagButton } from '../../../../../customerRequests/AskQuantityFlagButton';
import { qtyFlagRestaurants, qtyFlagRowRequests } from '../../../../../customerRequests/quantityFlagRestaurant';
import { useState } from 'react';
import { cn } from '../../../../../../lib/utils';
import { primaryText, secondaryText, semiBoldText } from '../../../../../../styling/stylingHelper';
import type { FlaggedProductWithRestaurant } from '../../../../../../services/admin/adminService';
import { PlatformCopyCell, PromptCopyCell } from '../../shared/copyCells';
import { FlaggedProductDetailButton } from '../../quantityAudit/FlaggedProductDetailButton';
import { QtyFlagRowMenu } from '../../quantityAudit/QtyFlagRowMenu';
import { QtyFlagKnownButton } from '../knownCases/markKnownButtons';
import { ApplyOverrideButton } from './ApplyOverrideButton';
import {
  QtyFlagDoneButton,
  QtyFlagMarkAllDoneButton,
  QtyFlagRecheckAllButton,
  QtyFlagRecheckButton,
  QtyFlagSendAllMonkeyButton,
  QtyFlagSendSelectedButton,
  QtyFlagUndoKnownButton,
  SourceBadge,
} from './qtyFlagButtons';
import { QtyFlagRequestStatus } from './QtyFlagRequestStatus';
import { aiQtyFlagKey } from './aiQtyFlagKey';
import { groupFlagsByProduct, isAskCustomerFlag, qtyFlagProductKey, qtyFlagRowKeys } from './qtyFlagGrouping';
import type { TFn } from '../../types';

// One grouped-by-platform table of quantity-info flags — same row format (with copy-fix-prompt) as the
// standalone quantity audit. `variant` switches the per-row action: open = mark done/known, done = undo
// done, known = undo known. `uniqueByProduct` paints one row per product name; that row's done/known
// then act on every flag it stands for, otherwise the next duplicate would just take its place.
export function QtyFlagTable({ flags, uniqueByProduct = false, variant, onChanged, onMarkedKnown, t, selected, onToggleSelect, onSendSelected }: { flags: FlaggedProductWithRestaurant[]; uniqueByProduct?: boolean; variant: 'open' | 'done' | 'known'; onChanged: (key: string) => void; onMarkedKnown?: () => void; t: TFn; selected?: Set<string>; onToggleSelect?: (key: string) => void; onSendSelected?: (products: FlaggedProductWithRestaurant[]) => Promise<void> }) {
  const selectable = !!onToggleSelect;
  // Render ceiling. The query behind this list is deliberately UNCAPPED (a work queue that truncates
  // server-side hides work and under-reports the count), so the throttle lives here instead: each flag
  // paints a row plus a full action set, and a few thousand of those lock the page up.
  // Everything that ISN'T rendering — the per-supplier count in the header and the "recheck all" /
  // "mark all done" / "send all" buttons — keeps using the FULL group, so a bulk action on a
  // partially-rendered supplier still covers every one of its flags.
  const RENDER_STEP = 100;
  const [renderLimit, setRenderLimit] = useState(RENDER_STEP);

  const byPlatform = new Map<string, FlaggedProductWithRestaurant[]>();
  for (const f of flags) {
    if (!byPlatform.has(f.platform)) byPlatform.set(f.platform, []);
    byPlatform.get(f.platform)!.push(f);
  }
  const flagsByProduct = groupFlagsByProduct(flags);
  const rowKeys = qtyFlagRowKeys(flags);
  // A de-duplicated row outlives the flag it happens to show, so its recheck pill survives that flag closing.
  const rowKeyOf = (p: FlaggedProductWithRestaurant) => (uniqueByProduct ? qtyFlagProductKey(p) : rowKeys.get(p));
  const duplicatesOf = (p: FlaggedProductWithRestaurant) => (uniqueByProduct ? flagsByProduct.get(qtyFlagProductKey(p)) : undefined) ?? [p];
  const markChanged = (p: FlaggedProductWithRestaurant) => duplicatesOf(p).forEach((d) => onChanged(aiQtyFlagKey(d)));
  const rowsOf = (products: FlaggedProductWithRestaurant[]) => (uniqueByProduct ? products.filter((p) => duplicatesOf(p)[0] === p) : products);
  const totalRows = Array.from(byPlatform.values()).reduce((sum, products) => sum + rowsOf(products).length, 0);
  const duplicateBadge = (p: FlaggedProductWithRestaurant) => duplicatesOf(p).length > 1 && (
    <span
      title={t('adminTesting.review.quantityFlagsDuplicates', { count: duplicatesOf(p).length })}
      className="shrink-0 px-1.5 py-0.5 rounded text-[10px] font-medium whitespace-nowrap bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200"
    >
      ×{duplicatesOf(p).length}
    </span>
  );
  const askRestaurantTag = (p: FlaggedProductWithRestaurant) => uniqueByProduct && !p.setupSource && isAskCustomerFlag(p) && qtyFlagRestaurants(p).length > 0 && (
    <span className={cn(secondaryText, 'block font-sans')}>{qtyFlagRestaurants(p).map((r) => r.restaurantName).join(', ')}</span>
  );
  const recheckButton = (p: FlaggedProductWithRestaurant) => (
    <QtyFlagRecheckButton flag={p} duplicates={duplicatesOf(p)} onResolved={variant === 'open' ? (keys) => keys.forEach(onChanged) : undefined} t={t} />
  );
  // One shared budget spent across the platform groups in order — a group beyond it isn't rendered at
  // all, and its flags are counted in the footer instead.
  let budget = renderLimit;
  const groups = Array.from(byPlatform.entries())
    .map(([platform, products]) => {
      const visibleProducts = rowsOf(products).slice(0, Math.max(0, budget));
      budget -= visibleProducts.length;
      return { platform, products, visibleProducts };
    })
    .filter((g) => g.visibleProducts.length > 0);
  const hidden = Math.max(0, totalRows - renderLimit);

  return (
    <>
      {groups.map(({ platform, products, visibleProducts }) => {
        const selectedProducts = selected ? products.filter((p) => selected.has(aiQtyFlagKey(p))) : [];
        return (
        <div key={platform} className="mb-3 last:mb-0">
          <div className={cn(primaryText, semiBoldText, 'text-xs mb-1 font-mono flex items-center gap-2 max-sm:flex-wrap')}>
            <span>{platform} <span className={secondaryText}>({products.length}{uniqueByProduct ? ` · ${t('adminTesting.review.quantityFlagsUnique', { count: rowsOf(products).length })}` : ''})</span></span>
            {variant === 'open' && (
              <>
                <QtyFlagRecheckAllButton
                  platform={platform}
                  products={products}
                  onResolved={(keys) => keys.forEach(onChanged)}
                  t={t}
                />
                <QtyFlagMarkAllDoneButton
                  platform={platform}
                  products={products}
                  onDone={() => products.forEach((p) => onChanged(aiQtyFlagKey(p)))}
                  t={t}
                />
                <QtyFlagSendAllMonkeyButton platform={platform} products={products} t={t} />
                {onSendSelected && selectedProducts.length > 0 && (
                  <QtyFlagSendSelectedButton products={selectedProducts} onSend={onSendSelected} t={t} />
                )}
              </>
            )}
          </div>
          {/* Desktop/tablet: the full table. Phones get the sm:hidden card list below instead. */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className={cn(secondaryText, 'border-b border-gray-200 dark:border-gray-600')}>
                  {selectable && <th className="py-1 px-2 w-6"></th>}
                  <th className="py-1 px-2 w-6"></th>
                  <th className="text-left py-1 px-2">{t('adminTesting.qtyAuditAll.col.articleId')}</th>
                  <th className="text-left py-1 px-2">{t('adminTesting.qtyAuditAll.col.product')}</th>
                  <th className="text-left py-1 px-2">{t('adminTesting.qtyAuditAll.col.quantityInfo')}</th>
                  <th className="text-right py-1 px-2">{t('adminTesting.qtyAuditAll.col.unitPrice')}</th>
                  <th className="text-left py-1 px-2">{t('adminTesting.review.labelReason')}</th>
                  <th className="text-left py-1 px-2">{t('adminTesting.review.quantitySourceColumn')}</th>
                  <th className="py-1 px-2"></th>
                </tr>
              </thead>
              <tbody>
                {visibleProducts.map((p, pi) => (
                  <tr key={rowKeyOf(p)} className={cn('border-b border-gray-100 dark:border-gray-700', pi % 2 === 0 ? 'bg-gray-50/50 dark:bg-gray-800/30' : '')}>
                    {selectable && (
                      <td className="py-1 px-2 text-center">
                        <input
                          type="checkbox"
                          checked={selected?.has(aiQtyFlagKey(p)) ?? false}
                          onChange={() => onToggleSelect!(aiQtyFlagKey(p))}
                          className="cursor-pointer"
                        />
                      </td>
                    )}
                    <td className="py-1 px-2 text-center"><PromptCopyCell product={p} /></td>
                    <td className={cn(primaryText, 'py-1 px-2 font-mono')}>{p.articleId || '-'}</td>
                    <td className={cn(primaryText, 'py-1 px-2 max-w-xs')} title={p.productName}>
                      <div className="flex items-center gap-1.5">
                        <span className="truncate">{p.productName}</span>
                        <SourceBadge source={p.source} t={t} />
                        {duplicateBadge(p)}
                      </div>
                    </td>
                    <td className={cn(secondaryText, 'py-1 px-2 font-mono whitespace-nowrap')}>
                      {p.quantityInfo ? <span>{p.quantityInfo.stdMultiplier} x {p.quantityInfo.stdAmount}{p.quantityInfo.stdUnit}</span> : <span className="text-red-500 dark:text-textRed-dark">null</span>}
                    </td>
                    <td className={cn(primaryText, 'py-1 px-2 font-mono text-right')}>€{p.unitPrice.toFixed(2)}</td>
                    <td className={cn('py-1 px-2 text-orange-600 dark:text-orange-400')}>{p.reason}<QtyFlagRequestStatus requests={qtyFlagRowRequests(duplicatesOf(p))} t={t} /></td>
                    <td className={cn(primaryText, 'py-1 px-2 font-mono')}>{p.setupSource ? <span className={secondaryText}>{t('adminTesting.review.setupUploadSource')} · {p.restaurantName}</span> : <PlatformCopyCell platform={p.platform} gmailId={p.gmailId} />}{askRestaurantTag(p)}</td>
                    <td className="py-1 px-2 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {recheckButton(p)}
                        <FlaggedProductDetailButton product={p} t={t} />
                        <ApplyOverrideButton flag={p} t={t} />
                        <AskQuantityFlagButton flag={p} duplicates={duplicatesOf(p)} />
                        {variant === 'known' ? (
                          <QtyFlagUndoKnownButton flag={p} onUndone={() => { onChanged(aiQtyFlagKey(p)); onMarkedKnown?.(); }} />
                        ) : (
                          <>
                            {variant === 'open' && <QtyFlagKnownButton flag={p} duplicates={duplicatesOf(p)} onDone={() => { markChanged(p); onMarkedKnown?.(); }} t={t} />}
                            <QtyFlagDoneButton flag={p} duplicates={duplicatesOf(p)} resolved={variant === 'done'} onDone={() => markChanged(p)} />
                          </>
                        )}
                        <QtyFlagRowMenu flag={p} t={t} isAuditTrace />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Phone: one card per flag, same fields and same action set as the table row. */}
          <div className="sm:hidden space-y-2">
            {visibleProducts.map((p) => (
              <div key={rowKeyOf(p)} className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/30 p-3 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-start gap-1.5 min-w-0">
                    {selectable && (
                      <input
                        type="checkbox"
                        checked={selected?.has(aiQtyFlagKey(p)) ?? false}
                        onChange={() => onToggleSelect!(aiQtyFlagKey(p))}
                        className="cursor-pointer mt-0.5"
                      />
                    )}
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className={cn(primaryText, 'text-xs font-medium break-words')}>{p.productName}</span>
                        <SourceBadge source={p.source} t={t} />
                        {duplicateBadge(p)}
                      </div>
                      <div className={cn(secondaryText, 'text-[10px] font-mono')}>{p.articleId || '-'}</div>
                    </div>
                  </div>
                  <span className={cn(primaryText, 'text-xs font-mono whitespace-nowrap shrink-0')}>€{p.unitPrice.toFixed(2)}</span>
                </div>
                <div className={cn(secondaryText, 'text-xs font-mono')}>
                  {t('adminTesting.qtyAuditAll.col.quantityInfo')}:{' '}
                  {p.quantityInfo
                    ? <span>{p.quantityInfo.stdMultiplier} x {p.quantityInfo.stdAmount}{p.quantityInfo.stdUnit}</span>
                    : <span className="text-red-500 dark:text-textRed-dark">null</span>}
                </div>
                <div className="text-xs text-orange-600 dark:text-orange-400 break-words">{p.reason}<QtyFlagRequestStatus requests={qtyFlagRowRequests(duplicatesOf(p))} t={t} /></div>
                <div className={cn(primaryText, 'text-xs font-mono')}>{p.setupSource ? <span className={secondaryText}>{t('adminTesting.review.setupUploadSource')} · {p.restaurantName}</span> : <PlatformCopyCell platform={p.platform} gmailId={p.gmailId} />}{askRestaurantTag(p)}</div>
                <div className="flex flex-wrap items-center gap-1 pt-2 border-t border-gray-100 dark:border-gray-700">
                  <PromptCopyCell product={p} />
                  {recheckButton(p)}
                  <FlaggedProductDetailButton product={p} t={t} />
                  <ApplyOverrideButton flag={p} t={t} />
                  <AskQuantityFlagButton flag={p} duplicates={duplicatesOf(p)} />
                  {variant === 'known' ? (
                    <QtyFlagUndoKnownButton flag={p} onUndone={() => { onChanged(aiQtyFlagKey(p)); onMarkedKnown?.(); }} />
                  ) : (
                    <>
                      {variant === 'open' && <QtyFlagKnownButton flag={p} duplicates={duplicatesOf(p)} onDone={() => { markChanged(p); onMarkedKnown?.(); }} t={t} />}
                      <QtyFlagDoneButton flag={p} duplicates={duplicatesOf(p)} resolved={variant === 'done'} onDone={() => markChanged(p)} />
                    </>
                  )}
                  <QtyFlagRowMenu flag={p} t={t} isAuditTrace />
                </div>
              </div>
            ))}
          </div>
        </div>
        );
      })}
      {hidden > 0 && (
        <div className="flex flex-wrap justify-center items-center gap-2 pt-3">
          <button
            onClick={() => setRenderLimit((n) => n + RENDER_STEP)}
            className={cn('px-4 py-2 rounded-lg text-xs', semiBoldText,
              'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600')}
          >
            {t('adminTesting.review.loadMore', { step: Math.min(RENDER_STEP, hidden), remaining: hidden })}
          </button>
          <button
            onClick={() => setRenderLimit(totalRows)}
            className={cn(secondaryText, 'text-xs underline hover:text-purple-600 dark:hover:text-purple-400')}
          >
            {t('adminTesting.review.loadAll', { count: totalRows })}
          </button>
        </div>
      )}
    </>
  );
}
